import BuildersLogo from "@components/BuildersLogo";
import HeadlessButton from "@components/Button";
import HeadlessDialog from "@components/Dialog";
import SectionHeading from "@components/SectionHeading";
import Skeleton from "@components/Skeleton";
import Spinner from "@components/Spinner";
import FormikCombobox from "@components/form/FormikCombobox";
import FormikDatepicker from "@components/form/FormikDatepicker";
import FormikInput from "@components/form/FormikInput";
import FormikRadioGroup from "@components/form/FormikRadioGroup";
import { buildWorkerPayload } from "@factory/buildWorkerPayload";
import { iFormValues } from "@models/forms/iFormValues";
import { CheckRounded } from "@mui/icons-material";
import { fetchPlants, PlantOption } from "@service/PlantService";
import FacilityService, { FacilityOption } from "@service/FacilityService";
import { createContractor } from "@service/contractorService";
import { countries } from "@utils/countries";
import { states } from "@utils/states";
import createContractorSchema from "@validators/createContractorSchema";
import { Field, Form, Formik, FormikHelpers } from "formik";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

export default function CreateNewContractor() {
  const { t } = useTranslation();

  const city = t("CreateNewContractor.city");
  const zipCode = t("CreateNewContractor.zipCode");
  const state = t("CreateNewContractor.state");
  const country = t("CreateNewContractor.country");
  const email = t("CreateNewContractor.email");
  const phone = t("CreateNewContractor.phoneNum");
  const plant = t("CreateNewContractor.plant");
  const facility = t("CreateNewContractor.facility");
  const contractorId = t("CreateNewContractor.contractorId");
  const rent = t("CreateNewContractor.rent");
  const admin = t("CreateNewContractor.admin");
  const misc = t("CreateNewContractor.misc");

  const [isLoading, setIsLoading] = useState(false);
  const [isPageLoading, setIsPageLoading] = useState(true);
  const [success, setSuccess] = useState(false);
  const [plants, setPlants] = useState<PlantOption[]>([]);
  const [facilities, setFacilities] = useState<FacilityOption[]>([]);
  const [showDialog, setShowDialog] = useState(false);
  const [dialogTitle, setDialogTitle] = useState("");
  const [dialogDescription, setDialogDescription] = useState("");
  const [dialogContext, setDialogContext] = useState("");
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  const initialValues: iFormValues = {
    firstName: "",
    secondFirstName: "",
    lastName: "",
    secondLastName: "",
    city: "",
    state: "",
    streetAddress: "",
    zipCode: "",
    country: "",
    birthDate: new Date(),
    preferredLang: "",
    email: "",
    phone: "",
    plant: "",
    facility: "",
    contractorId: "",
    firstWorkingDay: new Date(),
    driversLicense: "",
    bankAccountNumber: "",
    employeeType: "",
    payRateClass: "",
    billingRateClass: "",
    payRate: "",
    billingRate: "",
    taxWithHolding: "",
    payType: "",
    rentDeduction: "",
    adminDeduction: "",
    visaBondDeduction: "",
    miscDeduction: "",
    isActive: true,
    engagementStatus: "",
    engagementStartDate: new Date(),
  };

  const validationSchema = createContractorSchema(t);

  useEffect(() => {
    fetchPlants()
      .then((formattedPlants) => {
        setPlants(formattedPlants);
      })
      .catch((error) => {
        setDialogContext("plants");
        setDialogTitle(t("CreateNewContractor.error"));
        setDialogDescription(
          t("CreateNewContractor.errorFetchingPlants", { error })
        );
        setShowDialog(true);
      })
      .finally(() => {
        setIsPageLoading(false);
      });
  }, [t]);

  useEffect(() => {
    function handleResize() {
      setIsMobile(window.innerWidth <= 768);
    }

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  function skeletonLength() {
    return Math.round(window.innerHeight / 100);
  }

  // Facilities are scoped to the selected plant.
  async function loadFacilities(plantId?: number) {
    if (!plantId) {
      setFacilities([]);
      return;
    }
    try {
      const options = await FacilityService.getFacilityOptionsByPlant(plantId);
      setFacilities(options);
    } catch (error) {
      console.error("Error fetching facilities:", error);
      setFacilities([]);
    }
  }

  // — SUBMIT HANDLER uses factory + service —
  async function onSubmit(
    values: iFormValues,
    formikHelpers: FormikHelpers<iFormValues>
  ) {
    formikHelpers.setSubmitting(true);
    setIsLoading(true);
    try {
      const payload = buildWorkerPayload(values, plants, facilities);
      /* @ts-ignore */
      await createContractor(payload);
      setSuccess(true);
      setDialogContext("contractor");
      setDialogTitle(t("CreateNewContractor.success"));
      setDialogDescription(t("CreateNewContractor.contractorSuccess"));
    } catch (err) {
      setDialogTitle(t("CreateNewContractor.error"));
      setDialogDescription(
        t("CreateNewContractor.contractorError", { error: err })
      );
    } finally {
      setIsLoading(false);
      formikHelpers.setSubmitting(false);
      setShowDialog(true);
    }
  }

  return (
    <div className="tw-py-6">
      {isPageLoading ? (
        <>
          <div className="tw-flex tw-justify-center tw-items-center tw-mt-8">
            <BuildersLogo data-testid="builders-logo" />
          </div>
          {Array.from({ length: skeletonLength() }).map((_, index) => (
            <div
              key={index}
              className="tw-flex tw-justify-center tw-items-center tw-gap-6 tw-px-16 tw-pt-10"
            >
              {isMobile ? (
                <Skeleton
                  numberOfSkeletons={1}
                  className="tw-h-9 tw-w-screen tw-rounded-md"
                  data-testid="skeleton"
                />
              ) : (
                <Skeleton
                  numberOfSkeletons={2}
                  className="tw-h-9 tw-w-[40%] tw-rounded-md"
                  data-testid="skeleton"
                />
              )}
            </div>
          ))}

          <div className="tw-flex tw-justify-center tw-items-center tw-pt-10">
            <Skeleton
              numberOfSkeletons={1}
              className="tw-h-9 tw-w-[20%] tw-rounded-md"
            />
          </div>
        </>
      ) : (
        <>
          {showDialog && (
            <HeadlessDialog
              title={dialogTitle}
              description={dialogDescription}
              isOpen={showDialog}
              onClose={() =>
                dialogContext === "contractor"
                  ? (window.location.href = "/react/roster")
                  : null
              }
              setIsOpen={setShowDialog}
            >
              <></>
            </HeadlessDialog>
          )}
          <h1 className="tw-text-2xl tw-font-bold">
            {t("CreateNewContractor.title")}
          </h1>
          <Formik<iFormValues>
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={onSubmit}
          >
            {({ values, errors, touched, setFieldValue }) => (
              <div className="tw-px-6 tw-pb-6" data-testid="form">
                <Form>
                  <SectionHeading className="tw-mt-6">
                    {t("CreateNewContractor.personalInfo")}
                  </SectionHeading>
                  <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
                    <div>
                      <Field
                        name="firstName"
                        label={t("CreateNewContractor.firstNameLabel")}
                        as={FormikInput}
                        error={touched.firstName && errors.firstName}
                      />
                    </div>
                    <div>
                      <Field
                        name="secondFirstName"
                        label={t("CreateNewContractor.secondFirstNameLabel")}
                        as={FormikInput}
                        error={
                          touched.secondFirstName && errors.secondFirstName
                        }
                      />
                    </div>
                    <div>
                      <Field
                        name="lastName"
                        label={t("CreateNewContractor.lastNameLabel")}
                        as={FormikInput}
                        error={touched.lastName && errors.lastName}
                      />
                    </div>
                    <div>
                      <Field
                        name="secondLastName"
                        label={t("CreateNewContractor.secondLastNameLabel")}
                        as={FormikInput}
                        error={touched.secondLastName && errors.secondLastName}
                      />
                    </div>
                    <div>
                      <Field
                        name="streetAddress"
                        label={t("CreateNewContractor.streetAddressLabel")}
                        as={FormikInput}
                        error={touched.streetAddress && errors.streetAddress}
                      />
                    </div>
                    <div>
                      <Field
                        name="city"
                        label={city}
                        as={FormikInput}
                        error={touched.city && errors.city}
                      />
                    </div>
                    <div>
                      <Field
                        name="zipCode"
                        label={zipCode}
                        as={FormikInput}
                        error={touched.zipCode && errors.zipCode}
                      />
                    </div>
                    <div>
                      <Field
                        name="state"
                        label={state}
                        options={states}
                        // pageSize={0}
                        as={FormikCombobox}
                        error={touched.state && errors.state}
                      />
                    </div>
                    <div>
                      <Field
                        name="country"
                        label={country}
                        options={countries}
                        as={FormikCombobox}
                        error={touched.country && errors.country}
                      />
                    </div>
                    <div>
                      <Field
                        name="birthDate"
                        label={t("CreateNewContractor.birthdateLabel")}
                        as={FormikDatepicker}
                        error={touched.birthDate && errors.birthDate}
                      />
                    </div>

                    <div>
                      <Field
                        name="preferredLang"
                        label={t("CreateNewContractor.preferredLangLabel")}
                        options={[
                          { value: t("CreateNewContractor.english") },
                          { value: t("CreateNewContractor.spanish") },
                          { value: t("CreateNewContractor.french") },
                          { value: t("CreateNewContractor.hatianCreole") },
                        ]}
                        as={FormikCombobox}
                        error={touched.preferredLang && errors.preferredLang}
                      />
                    </div>
                  </div>

                  <SectionHeading className="tw-mt-6">
                    {t("CreateNewContractor.contactInfo")}
                  </SectionHeading>
                  <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
                    <div>
                      <Field
                        name="email"
                        label={email}
                        as={FormikInput}
                        type="email"
                        error={touched.email && errors.email}
                      />
                    </div>

                    <div>
                      <Field
                        name="phone"
                        label={phone}
                        as={FormikInput}
                        inputMode="numeric"
                        error={touched.phone && errors.phone}
                      />
                    </div>
                  </div>
                  <SectionHeading className="tw-mt-6">
                    {t("CreateNewContractor.employmentInfo")}
                  </SectionHeading>
                  <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
                    <div>
                      <Field
                        name="plant"
                        label={plant}
                        options={plants}
                        as={FormikCombobox}
                        onOptionChange={(opt: PlantOption) => {
                          setFieldValue("facility", "");
                          loadFacilities(opt?.id);
                        }}
                        error={touched.plant && errors.plant}
                      />
                    </div>
                    <div>
                      <Field
                        name="facility"
                        label={facility}
                        options={facilities}
                        disabled={!values.plant}
                        as={FormikCombobox}
                        error={touched.facility && errors.facility}
                      />
                    </div>
                    <div>
                      <Field
                        name="contractorId"
                        label={contractorId}
                        as={FormikInput}
                        inputMode="numeric"
                        error={touched.contractorId && errors.contractorId}
                      />
                    </div>
                    <div>
                      <Field
                        name="firstWorkingDay"
                        label={t("CreateNewContractor.firstWorkDayLabel")}
                        as={FormikDatepicker}
                        error={
                          touched.firstWorkingDay && errors.firstWorkingDay
                        }
                      />
                    </div>
                    <div>
                      <Field
                        name="driversLicense"
                        label={t("CreateNewContractor.driversLicenseLabel")}
                        className="tw-flex tw-gap-6 tw-mt-1"
                        options={[
                          {
                            value: t("CreateNewContractor.yes"),
                            active: false,
                          },
                          { value: t("CreateNewContractor.no"), active: false },
                        ]}
                        as={FormikRadioGroup}
                        error={touched.driversLicense && errors.driversLicense}
                      />
                    </div>
                  </div>
                  <SectionHeading className="tw-mt-6">
                    {t("CreateNewContractor.paymentInfo")}
                  </SectionHeading>
                  <div className="tw-grid tw-grid-cols-1 tw-gap-4">
                    <div>
                      <Field
                        name="payType"
                        label={t("CreateNewContractor.payTypeLabel")}
                        options={[
                          { value: t("CreateNewContractor.check") },
                          { value: t("CreateNewContractor.directDeposit") },
                        ]}
                        as={FormikCombobox}
                        error={touched.payType && errors.payType}
                      />
                    </div>
                    {/* {values.payType ===
                      t("CreateNewContractor.directDeposit") && (
                      <div>
                        <Field
                          name="bankAccountNumber"
                          label={t(
                            "CreateNewContractor.bankAccountNumberLabel"
                          )}
                          as={FormikInput}
                          error={
                            touched.bankAccountNumber &&
                            errors.bankAccountNumber
                          }
                        />
                      </div>
                    )} */}

                    <div>
                      <Field
                        name="employeeType"
                        label={t("CreateNewContractor.employeeTypeLabel")}
                        className="tw-flex tw-gap-6 tw-mt-1"
                        options={[
                          { value: "W2", active: false },
                          { value: "1099", active: false },
                        ]}
                        as={FormikRadioGroup}
                        error={touched.employeeType && errors.employeeType}
                      />
                    </div>

                    {/* Pay-Rate Class */}
                    <div>
                      <Field
                        name="payRateClass"
                        label={t("CreateNewContractor.payRateClassLabel")}
                        className="tw-flex tw-gap-6 tw-mt-1"
                        options={[
                          {
                            value: "hourly",
                            label: t("CreateNewContractor.hourly"),
                          },
                          {
                            value: "daily",
                            label: t("CreateNewContractor.daily"),
                          },
                          {
                            value: "weekly",
                            label: t("CreateNewContractor.weekly"),
                          },
                        ]}
                        as={FormikRadioGroup}
                        error={touched.payRateClass && errors.payRateClass}
                      />
                    </div>

                    <div>
                      <Field
                        name="payRate"
                        label={t("CreateNewContractor.payRateLabel")}
                        as={FormikInput}
                        inputMode="numeric"
                        error={touched.payRate && errors.payRate}
                      />
                    </div>

                    {/* Billing-Rate Class (NEW) */}
                    <div>
                      <Field
                        name="billingRateClass"
                        label={t("CreateNewContractor.billingRateClassLabel")}
                        className="tw-flex tw-gap-6 tw-mt-1"
                        options={[
                          {
                            value: "hourly",
                            label: t("CreateNewContractor.hourly"),
                          },
                          {
                            value: "daily",
                            label: t("CreateNewContractor.daily"),
                          },
                          {
                            value: "weekly",
                            label: t("CreateNewContractor.weekly"),
                          },
                        ]}
                        as={FormikRadioGroup}
                        error={
                          touched.billingRateClass && errors.billingRateClass
                        }
                      />
                    </div>

                    <div>
                      <Field
                        name="billingRate"
                        label={t("CreateNewContractor.billingRateLabel")}
                        as={FormikInput}
                        inputMode="numeric"
                        error={touched.billingRate && errors.billingRate}
                      />
                    </div>

                    <div>
                      <Field
                        name="taxWithHolding"
                        label={t("CreateNewContractor.taxWithholdingLabel")}
                        as={FormikInput}
                        inputMode="numeric"
                        error={touched.taxWithHolding && errors.taxWithHolding}
                      />
                    </div>
                  </div>

                  <SectionHeading className="tw-mt-6">
                    {t("CreateNewContractor.deductions")}
                  </SectionHeading>

                  <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
                    <div>
                      <Field
                        name="rentDeduction"
                        label={rent}
                        as={FormikInput}
                        inputMode="numeric"
                        error={touched.rentDeduction && errors.rentDeduction}
                      />
                    </div>

                    <div>
                      <Field
                        name="adminDeduction"
                        label={admin}
                        as={FormikInput}
                        inputMode="numeric"
                        error={touched.adminDeduction && errors.adminDeduction}
                      />
                    </div>

                    <div>
                      <Field
                        name="visaBondDeduction"
                        label={t("CreateNewContractor.visaBondLabel")}
                        as={FormikInput}
                        inputMode="numeric"
                        error={
                          touched.visaBondDeduction && errors.visaBondDeduction
                        }
                      />
                    </div>

                    <div>
                      <Field
                        name="miscDeduction"
                        label={misc}
                        as={FormikInput}
                        inputMode="numeric"
                        error={touched.miscDeduction && errors.miscDeduction}
                      />
                    </div>
                  </div>
                  <div className="tw-flex tw-justify-center tw-mt-6">
                    {success ? (
                      <HeadlessButton
                        className="tw-w-full md:tw-w-[20%]"
                        theme="success"
                        data-testid="success-btn"
                        disabled={false}
                      >
                        <div className="tw-flex tw-justify-center tw-gap-2">
                          {t("CreateNewContractor.success")}
                          <CheckRounded className="size-4 tw-fill-white tw-pb-0.5" />
                        </div>
                      </HeadlessButton>
                    ) : (
                      <HeadlessButton
                        className="tw-w-full md:tw-w-[20%]"
                        type="submit"
                        data-testid="submit-btn"
                        disabled={isLoading}
                      >
                        {isLoading ? (
                          <div className="tw-flex tw-justify-center tw-align-middle tw-gap-2">
                            {t("CreateNewContractor.processing")}
                            <Spinner className="tw-mt-0.5" />
                          </div>
                        ) : (
                          t("CreateNewContractor.submit")
                        )}
                      </HeadlessButton>
                    )}
                  </div>
                </Form>
              </div>
            )}
          </Formik>
        </>
      )}
    </div>
  );
}
